import { Box, Button, CircularProgress, TextField, Typography } from '@mui/material'
import React from 'react'
import { vh_26 } from '../../../../constants/dimentions'
import * as PXBColors from '@brightlayer-ui/colors';
import { useLanguageLocale } from '../../../../hooks/language-locale-hooks';
import { INPUT_DESCRIPTION, INPUT_ZONE_LIMIT, ORGANIZATTION_NAME, RESTRICT_SPEC_CHARACTER } from '../../../../constants';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import { WhiteListZone, WhitelistZoneData } from '../../../../state/assets-state';
import { EmptyState } from '@brightlayer-ui/react-components';
import { Selectors } from '../../../../selectors';
import { AssetThunks } from '../../../../actions/thunks/assets-thunks';

type Props = {
    title: string;
    subtitle: string;
    projectId: string;
    deviceId: string;
    buildingId: string;
    deviceWhitelistZone: WhitelistZoneData | null;
    zoneData: { [id: number]: WhiteListZone };
};

const ZonesConfiguration: React.FC<React.PropsWithChildren<React.PropsWithChildren<Props>>> = (props: Props) => {
    const { t } = useLanguageLocale();
    const history = useHistory();
    const dispatch = useDispatch();
    const asset = useSelector(Selectors.Asset.assets);
    const isLoading = asset.getWhitelistDetailLoading || asset.whitelistzoneLoading;
    const [zoneNameErrors, setZoneNameErrors] = React.useState<{ [id: number]: boolean }>({});
    const [zoneDescErrors, setZoneDescErrors] = React.useState<{ [id: number]: boolean }>({});

    const { deviceWhitelistZone, zoneData, projectId, deviceId, buildingId } = props;

    // Use refs to store input values
    const nameRefs = React.useRef<{ [id: number]: HTMLInputElement | null }>({});
    const descriptionRefs = React.useRef<{ [id: number]: HTMLInputElement | null }>({});

    // Track if user is currently editing (to show Save/Cancel immediately)
    const [hasUnsavedChanges, setHasUnsavedChanges] = React.useState(false);
    const checkTimerRef = React.useRef<NodeJS.Timeout | null>(null);

    const zoneArray = [
        { id: 1, zone: 'Zone 1' },
        { id: 2, zone: 'Zone 2' },
        { id: 3, zone: 'Zone 3' },
        { id: 4, zone: 'Zone 4' },
        { id: 5, zone: 'Zone 5' },
        { id: 6, zone: 'Zone 6' },
        { id: 7, zone: 'Zone 7' },
        { id: 8, zone: 'Zone 8' },
        { id: 9, zone: 'Zone 9' },
        { id: 10, zone: 'Zone 10' },
        { id: 11, zone: 'Zone 11' },
        { id: 12, zone: 'Zone 12' },
        { id: 13, zone: 'Zone 13' },
        { id: 14, zone: 'Zone 14' },
        { id: 15, zone: 'Zone 15' },
        { id: 16, zone: 'Zone 16' },
    ];

    const luminariesCountMap: { [id: number]: number } = {};
    deviceWhitelistZone?.zones.forEach(zone => {
        luminariesCountMap[Number(zone.id)] = zone.luminariesCount;
    });

    React.useEffect(() => {
        for (let id = 1; id <= 16; id++) {
            // Get data from API if it exists, otherwise use empty strings
            const name = zoneData[id]?.name || '';
            const description = zoneData[id]?.description || '';
            if (nameRefs.current[id]) {
                nameRefs.current[id]!.value = name;
            }
            if (descriptionRefs.current[id]) {
                descriptionRefs.current[id]!.value = description;
            }
        }

        setHasUnsavedChanges(false);
    }, [zoneData]);

    const checkForChanges = React.useCallback(() => {
        let hasChanges = false;
        let hasIncompleteZone = false;
        const nameErrors: { [id: number]: boolean } = {};
        const descErrors: { [id: number]: boolean } = {};

        for (let id = 1; id <= 16; id++) {
            const currentName = nameRefs.current[id]?.value || '';
            const currentDesc = descriptionRefs.current[id]?.value || '';
            const originalName = zoneData[id]?.name?.trim() || '';
            const originalDesc = zoneData[id]?.description?.trim() || '';
            const trimmedCurrentName = currentName.trim();
            const trimmedCurrentDesc = currentDesc.trim();

            //  NEW: Check if BOTH fields were deleted (had data before, now empty)
            const hadNameBefore = originalName !== '';
            const hadDescBefore = originalDesc !== '';
            const bothFieldsNowEmpty = !trimmedCurrentName && !trimmedCurrentDesc;
            const bothFieldsWereFilled = hadNameBefore && hadDescBefore;

            // If user removed BOTH name and description that existed before, show errors
            if (bothFieldsWereFilled && bothFieldsNowEmpty) {
                hasIncompleteZone = true;
                nameErrors[id] = true;
                descErrors[id] = true;
            }
            //  Check if zone has name but no description (INVALID STATE)
            else if (trimmedCurrentName && !trimmedCurrentDesc) {
                hasIncompleteZone = true;
                descErrors[id] = true;
                nameErrors[id] = false;
            }
            //  Check if zone has description but no name (INVALID STATE)
            else if (trimmedCurrentDesc && !trimmedCurrentName) {
                hasIncompleteZone = true;
                nameErrors[id] = true;
                descErrors[id] = false;
            }
            //  Both empty and never had data before (valid - new empty zone)
            else {
                descErrors[id] = false;
                nameErrors[id] = false;
            }

            // Check for changes
            if (trimmedCurrentName !== originalName || trimmedCurrentDesc !== originalDesc) {
                hasChanges = true;
            }
        }

        //  Update error states
        setZoneNameErrors(nameErrors);
        setZoneDescErrors(descErrors);

        // Only allow save if changes exist AND no incomplete zones
        const canSave = hasChanges && !hasIncompleteZone;

        setHasUnsavedChanges(canSave);
    }, [zoneData]);

    const handleNameChange = (zoneId: number, e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;

        //  Only allow if empty OR matches regex
        if (value === '' || ORGANIZATTION_NAME.test(value)) {
            // Value is valid, allow it
            if (checkTimerRef.current) {
                clearTimeout(checkTimerRef.current);
            }

            checkTimerRef.current = setTimeout(() => {
                checkForChanges();
            }, 500);
        } else {
            //  Invalid character - restore previous valid value
            const previousValue = nameRefs.current[zoneId]?.value || '';
            // Remove the last invalid character
            const validValue = value.slice(0, -1);
            e.target.value = validValue;

            // Force update the ref
            if (nameRefs.current[zoneId]) {
                nameRefs.current[zoneId]!.value = validValue;
            }
        }
    };

    const handleDescriptionChange = (zoneId: number, e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;

        //  Only allow if empty OR matches regex
        if (value === '' || ORGANIZATTION_NAME.test(value)) {
            // Value is valid, allow it
            if (checkTimerRef.current) {
                clearTimeout(checkTimerRef.current);
            }

            checkTimerRef.current = setTimeout(() => {
                checkForChanges();
            }, 500);
        } else {
            //  Invalid character - restore previous valid value
            const previousValue = descriptionRefs.current[zoneId]?.value || '';
            // Remove the last invalid character
            const validValue = value.slice(0, -1);
            e.target.value = validValue;

            // Force update the ref
            if (descriptionRefs.current[zoneId]) {
                descriptionRefs.current[zoneId]!.value = validValue;
            }
        }
    };

    const handleNameBlur = (zoneId: number) => {
        if (checkTimerRef.current) {
            clearTimeout(checkTimerRef.current);
        }
        checkForChanges();
    };

    const handleDescriptionBlur = (zoneId: number) => {
        if (checkTimerRef.current) {
            clearTimeout(checkTimerRef.current);
        }
        checkForChanges();
    };

    const handleSave = () => {
        let hasIncompleteZone = false;

        for (let id = 1; id <= 16; id++) {
            const name = nameRefs.current[id]?.value?.trim() || '';
            const description = descriptionRefs.current[id]?.value?.trim() || '';

            // ✅ Check both: name without description OR description without name
            if ((name && !description) || (description && !name)) {
                hasIncompleteZone = true;
                break;
            }
        }

        if (hasIncompleteZone) {
            return; // Don't proceed with save
        }

        // Collect only zones that have BOTH name AND description
        const zonesPayload: WhiteListZone[] = [];

        for (let id = 1; id <= 16; id++) {
            const name = nameRefs.current[id]?.value?.trim() || '';
            const description = descriptionRefs.current[id]?.value?.trim() || '';

            // ✅ Only include zones that have BOTH name AND description
            if (name && description) {
                const luminariesCount = zoneData[id]?.luminariesCount || 0;

                zonesPayload.push({
                    id: id,
                    name: name,
                    description: description,
                    luminariesCount: luminariesCount,
                });
            }
        }

        // Check if we're editing existing zones or adding new ones
        const hasExistingZones = Object.keys(zoneData).length > 0;
        const whitelistId = hasExistingZones ? deviceWhitelistZone?.whitelistId : undefined;

        if (hasExistingZones) {
            dispatch(AssetThunks.manageWhitelistZone(
                deviceId,
                projectId,
                buildingId,
                zonesPayload,
                deviceWhitelistZone?.whitelistName || "Whitelist24NovShanti",
                whitelistId
            ));
        } else {
            dispatch(AssetThunks.manageWhitelistZone(
                deviceId,
                projectId,
                buildingId,
                zonesPayload,
                deviceWhitelistZone?.whitelistName || "Whitelist24NovShanti",
            ));
        }
    };




    const handleCancel = () => {

        // Reset ALL 16 zones to original values
        for (let id = 1; id <= 16; id++) {
            const originalName = zoneData[id]?.name || '';
            const originalDesc = zoneData[id]?.description || '';

            if (nameRefs.current[id]) {
                nameRefs.current[id]!.value = originalName;
            }
            if (descriptionRefs.current[id]) {
                descriptionRefs.current[id]!.value = originalDesc;
            }
        }

        setHasUnsavedChanges(false);

        // Force re-check
        setTimeout(() => {
            checkForChanges();
        }, 0);
    };

    // Cleanup timer on unmount
    React.useEffect(() => {
        return () => {
            if (checkTimerRef.current) {
                clearTimeout(checkTimerRef.current);
            }
        };
    }, []);

    if (isLoading) {
        return (
            <EmptyState
                style={{ flex: 1, minHeight: 400, backgroundColor: PXBColors.white[50] }}
                icon={<CircularProgress id="progress-spinner" />}
                title=""
                placeholder={undefined}
            />
        );
    }

    if (!asset.getWhitelistDetailSuccess) {
        return null;
    }


    return (
        <div data-testid="zones-configuration-container">
            <Box
                data-testid="zones-configuration"
                style={{
                    backgroundColor: 'white',
                    paddingLeft: '2%',
                    paddingTop: vh_26,
                    paddingRight: '5%',
                    backgroundSize: 'contain',
                    alignItems: 'center',
                }}
            >
                {/* Save/Cancel Buttons - Sticky at top */}
                {hasUnsavedChanges && (
                    <Box
                        sx={{
                            position: 'sticky',
                            top: 0,
                            zIndex: 100,
                            backgroundColor: PXBColors.blue[50],
                            padding: '12px 16px',
                            marginBottom: 2,
                            borderRadius: 1,
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                        }}
                    >
                        <Typography variant="body2" color={PXBColors.blue[700]}>
                            {t('ASSETS_DETAILS.ZONES_CONFIGURATION_UNSAVED_CHANGES')}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <Button
                                variant="outlined"
                                color="primary"
                                onClick={handleCancel}
                                data-testid="cancel-button"
                            >
                                {t('COMMON.CANCEL') || 'Cancel'}
                            </Button>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={handleSave}
                                data-testid="save-button"
                            >
                                {t('COMMON.SAVE') || 'Save'}
                            </Button>
                        </Box>
                    </Box>
                )}

                <Typography sx={{ display: 'flex', gap: '1%' }} data-testid='zone-config-description'>
                    <InfoOutlinedIcon /> {t('ASSETS_DETAILS.ZONE_CONFIG_DESC')}
                </Typography>

                {zoneArray.map(item => (
                    <Box key={item.id} data-testid={`zone-${item.id}`}>
                        <Box paddingBottom={"1%"} paddingTop={"2%"}>
                            <Typography fontWeight={600} fontSize={16}> {item.zone}</Typography>
                            <Typography>
                                {luminariesCountMap[item.id] !== undefined
                                    ? `${luminariesCountMap[item.id]} Luminaires`
                                    : '0 Luminaires'}
                            </Typography>
                        </Box>
                        <Box style={{
                            display: 'flex',
                            flexDirection: 'row',
                            gap: '5%',
                            justifyContent: 'start',
                        }}>
                            <Box style={{ width: '100%' }}>
                                <TextField
                                    disabled={asset.getWhitelistDetailLoading}
                                    inputRef={(el) => (nameRefs.current[item.id] = el)}
                                    defaultValue={zoneData[item.id]?.name || ''}
                                    id={`zone-name-${item.id}`}
                                    inputProps={{ 'data-testid': 'zoneName-field', maxLength: INPUT_ZONE_LIMIT }}
                                    variant="filled"
                                    fullWidth
                                    placeholder={t('ASSETS_DETAILS.PLACEHOLDER_TEXT')}
                                    label={t('ASSETS_DETAILS.ZONE_NAME')}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleNameChange(item.id, e)}
                                    onBlur={() => handleNameBlur(item.id)}
                                    error={zoneNameErrors[item.id]} // ✅ Show error state for name
                                    helperText={zoneNameErrors[item.id] ? t('TOAST_MSG.ZONE_NAME_REQUIRED') || 'Please add zone name to save this zone' : ''} // ✅ Error message
                                />
                                {!zoneNameErrors[item.id] && (
                                    <div data-testid='ZoneNameHelperTextId'
                                        style={{ height: 17, width: '100%', color: PXBColors.gray[500] }}>
                                        <small className="form-text" style={{ width: '85%', float: 'left', paddingLeft: '2%' }}>
                                            {t('ORGANIZATION_MANAGE.ORGANIZATION_NAME_HELPER')}</small>
                                        <Typography data-testid='zoneNameLimitId' style={{ width: 'auto', float: 'right', fontSize: 12, color: PXBColors.gray[500] }}>
                                            {nameRefs.current[item.id]?.value?.length || 0}/{INPUT_ZONE_LIMIT}
                                        </Typography>
                                    </div>
                                )}
                            </Box>
                            <Box style={{ width: '100%' }}>
                                <TextField
                                    disabled={asset.getWhitelistDetailLoading}
                                    inputRef={(el) => (descriptionRefs.current[item.id] = el)}
                                    defaultValue={zoneData[item.id]?.description || ''}
                                    id={`zone-description-${item.id}`}
                                    inputProps={{ 'data-testid': 'zone-description-field', maxLength: INPUT_DESCRIPTION }}
                                    variant="filled"
                                    fullWidth
                                    label={t('LOGBOOK_DETAILS.DESCRIPTION')}
                                    placeholder={t('ASSETS_DETAILS.PLACEHOLDER_TEXT')}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleDescriptionChange(item.id, e)}
                                    onBlur={() => handleDescriptionBlur(item.id)}
                                    error={zoneDescErrors[item.id]} // ✅ Show error state for description
                                    helperText={zoneDescErrors[item.id] ? t('TOAST_MSG.ZONE_DESCRIPTION_REQUIRED') || 'Please add description to save this zone' : ''} // ✅ Error message
                                />
                                {/* ✅ Show character count only when no error */}
                                {!zoneDescErrors[item.id] && (
                                    <div data-testid='zoneDescriptionHelperTextId'
                                        style={{ height: 17, width: '100%', color: PXBColors.gray[500] }}>
                                        <small className="form-text" style={{ width: '85%', float: 'left', paddingLeft: '2%' }}>
                                            {t('ORGANIZATION_MANAGE.ORGANIZATION_NAME_HELPER')}</small>
                                        <Typography data-testid='ZoneDescriptionCharLengthId' style={{ width: 'auto', float: 'right', fontSize: 12, color: PXBColors.gray[500] }}>
                                            {descriptionRefs.current[item.id]?.value?.length || 0}/{INPUT_DESCRIPTION}
                                        </Typography>
                                    </div>
                                )}
                            </Box>
                        </Box>
                    </Box>
                ))}
            </Box>
        </div>
    );
}

export default React.memo(ZonesConfiguration);
